import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Home() {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<string>('');

  const roles = [
    {
      id: 'admin',
      name: '系统管理员',
      icon: 'ri-admin-line',
      color: 'bg-purple-500',
      description: '管理系统用户、权限和整体配置',
      path: '/admin/dashboard'
    },
    {
      id: 'purchaser',
      name: '采购方',
      icon: 'ri-building-line',
      color: 'bg-blue-500',
      description: '发布招标项目、评审投标、签订合同',
      path: '/purchaser/dashboard'
    },
    {
      id: 'agent',
      name: '招标代理',
      icon: 'ri-service-line',
      color: 'bg-cyan-500',
      description: '代理招标业务、协助采购方完成招标流程',
      path: '/agent/dashboard'
    },
    {
      id: 'bidder',
      name: '投标方',
      icon: 'ri-briefcase-line',
      color: 'bg-green-500',
      description: '查看招标信息、提交投标文件、跟踪项目',
      path: '/bidder/dashboard'
    },
    {
      id: 'expert',
      name: '评审专家',
      icon: 'ri-user-star-line',
      color: 'bg-orange-500',
      description: '评审投标文件、打分、提供专业意见',
      path: '/expert/dashboard'
    },
    {
      id: 'supervisor',
      name: '监督方',
      icon: 'ri-shield-check-line',
      color: 'bg-red-500',
      description: '监督招投标过程、确保合规性',
      path: '/supervisor/dashboard'
    }
  ];

  const workflowSteps = [
    {
      step: 1,
      title: '项目立项',
      description: '采购方创建招标项目',
      icon: 'ri-file-add-line',
      role: '采购方'
    },
    {
      step: 2,
      title: '发布公告',
      description: '公开发布招标公告',
      icon: 'ri-megaphone-line',
      role: '采购方'
    },
    {
      step: 3,
      title: '报名投标',
      description: '投标方报名参与',
      icon: 'ri-user-add-line',
      role: '投标方'
    },
    {
      step: 4,
      title: '提交标书',
      description: '投标方提交投标文件',
      icon: 'ri-file-upload-line',
      role: '投标方'
    },
    {
      step: 5,
      title: '开标评审',
      description: '专家评审投标文件',
      icon: 'ri-file-search-line',
      role: '评审专家'
    },
    {
      step: 6,
      title: '中标公示',
      description: '公示中标结果',
      icon: 'ri-trophy-line',
      role: '采购方'
    },
    {
      step: 7,
      title: '合同签订',
      description: '签订项目合同',
      icon: 'ri-file-text-line',
      role: '采购方/投标方'
    },
    {
      step: 8,
      title: '项目执行',
      description: '执行项目并监督',
      icon: 'ri-settings-line',
      role: '监督方'
    }
  ];

  const handleRoleSelect = (path: string) => {
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                <i className="ri-auction-line text-2xl text-white"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">智慧招投标管理系统</h1>
                <p className="text-sm text-gray-500">全流程数字化招投标平台</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button className="px-4 py-2 text-gray-600 hover:text-gray-900 whitespace-nowrap cursor-pointer">
                <i className="ri-notification-line text-xl"></i>
              </button>
              <button className="px-4 py-2 text-gray-600 hover:text-gray-900 whitespace-nowrap cursor-pointer">
                <i className="ri-settings-3-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Welcome Section */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">欢迎使用招投标管理系统</h2>
          <p className="text-xl text-gray-600">请选择您的角色进入对应的工作场景</p>
        </div>

        {/* Role Selection */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">选择角色</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {roles.map((role) => (
              <div
                key={role.id}
                onClick={() => handleRoleSelect(role.path)}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-2 border-2 border-transparent hover:border-blue-500 p-8"
              >
                <div className={`w-16 h-16 ${role.color} rounded-xl flex items-center justify-center mb-6`}>
                  <i className={`${role.icon} text-3xl text-white`}></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900 mb-3">{role.name}</h4>
                <p className="text-gray-600 leading-relaxed">{role.description}</p>
                <div className="mt-6 flex items-center text-blue-600 font-semibold">
                  <span className="whitespace-nowrap">进入工作台</span>
                  <i className="ri-arrow-right-line ml-2"></i>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Workflow Section */}
        <div className="bg-white rounded-3xl shadow-xl p-10 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-10 text-center">招投标工作流程</h3>
          <div className="relative">
            {/* Workflow Steps */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {workflowSteps.map((step, index) => (
                <div key={step.step} className="relative">
                  <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl flex items-center justify-center">
                          <i className={`${step.icon} text-xl text-white`}></i>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-600 text-white text-xs font-bold rounded-full">
                            {step.step}
                          </span>
                          <h4 className="text-lg font-bold text-gray-900">{step.title}</h4>
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{step.description}</p>
                        <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full whitespace-nowrap">
                          {step.role}
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Arrow */}
                  {index < workflowSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <i className="ri-arrow-right-s-line text-3xl text-blue-400"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Core Features */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-8 text-white">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-shield-check-line text-3xl"></i>
            </div>
            <h4 className="text-xl font-bold mb-3">安全可靠</h4>
            <p className="text-blue-100 leading-relaxed">采用先进的加密技术和权限管理，确保数据安全</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-8 text-white">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-speed-line text-3xl"></i>
            </div>
            <h4 className="text-xl font-bold mb-3">高效便捷</h4>
            <p className="text-purple-100 leading-relaxed">全流程在线操作，大幅缩短招投标周期，提升工作效率</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-8 text-white">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
              <i className="ri-eye-line text-3xl"></i>
            </div>
            <h4 className="text-xl font-bold mb-3">公开透明</h4>
            <p className="text-green-100 leading-relaxed">全程公开透明，接受各方监督，确保公平公正</p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-center text-white">
          <h3 className="text-2xl font-bold mb-6">立即体验智慧招投标管理系统</h3>
          <p className="text-xl leading-relaxed mb-8">全流程数字化招投标平台，让招投标更高效、更透明、更安全</p>
          <button className="px-8 py-3 text-xl font-bold bg-white text-blue-900 rounded-full transition-all duration-300 hover:scale-110 whitespace-nowrap">
            <i className="ri-play-circle-line mr-3"></i>
            开始使用
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-400">版权所有 成都云化信息技术有限公司 蜀ICP备2024085016号-4</p>
          <div className="mt-4">
            <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-white whitespace-nowrap cursor-pointer">Powered by ChengduYunHua</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
